import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  LockOpen as LockOpenIcon,
  Language as LanguageIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import GlobalConfig from '../../core/api/GlobalConfig';
import { SUPPORTED_LANGUAGES } from '../../core/i18n/languages';
import { useCaseStatusLanguage } from './CaseStatusLanguageProvider';

// Loaded once — not inside component to avoid re-evaluation on every render
const defaultLogo = require('../../assets/images/logo.svg').default;

type CaseStatusLayoutProps = {
  children: React.ReactNode;
};

const CaseStatusLayout = ({ children }: CaseStatusLayoutProps) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const useLegacyLogin = GlobalConfig.val().UseLegacyLogin;
  const loginPath = useLegacyLogin ? '/' : '/signin';
  const { language, setLanguage, t } = useCaseStatusLanguage();
  
  useEffect(() => {
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';

    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';

    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&display=swap';

    document.head.appendChild(preconnect1);
    document.head.appendChild(preconnect2);
    document.head.appendChild(fontLink);

    return () => {
      document.head.removeChild(preconnect1);
      document.head.removeChild(preconnect2);
      document.head.removeChild(fontLink);
    };
  }, []);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  const headerFooterBg = theme.palette.primary.main;
  const headerFooterText = theme.palette.primary.contrastText;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.palette.background.default }}>

      {/* Header */}
      <Box component="header" sx={{ backgroundColor: headerFooterBg, height: 60, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm" sx={{ display: 'flex', alignItems: 'center', height: '100%', px: { xs: 2, sm: 3 } }}>
          <Link to={loginPath} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img src={defaultLogo} alt="My Page" style={{ height: 36, width: 'auto' }} />
          </Link>

          <IconButton
            onClick={handleMenuOpen}
            aria-label="Open menu"
            aria-controls={menuAnchor ? 'public-nav-menu' : undefined}
            aria-haspopup="true"
            sx={{ color: headerFooterText, ml: 'auto' }}
          >
            <MenuIcon />
          </IconButton>

          <Menu
            id="public-nav-menu"
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem component={Link} to={loginPath} onClick={handleMenuClose}>
              <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('Home')}</ListItemText>
            </MenuItem>
            <MenuItem component={Link} to={loginPath} onClick={handleMenuClose}>
              <ListItemIcon><LockOpenIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{t('LogInWithBankID')}</ListItemText>
            </MenuItem>

            <Divider />
            <ListSubheader
              disableSticky
              sx={{ display: 'flex', alignItems: 'center', gap: 1, lineHeight: 2.5, bgcolor: 'transparent' }}
            >
              <LanguageIcon fontSize="small" />
              {t('LanguagesMenuLink')}
            </ListSubheader>
            <Box data-testid="language-selector">
              {SUPPORTED_LANGUAGES.map((l) => (
                <MenuItem
                  key={l.code}
                  selected={l.code === language}
                  data-testid={`language-option-${l.code}`}
                  onClick={() => {
                    setLanguage(l.code);
                    handleMenuClose();
                  }}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}
                >
                  <Typography component="span" sx={{ fontWeight: l.code === language ? 700 : 400 }}>
                    {l.label}
                  </Typography>
                  {l.code === language ? (
                    <CheckIcon fontSize="small" color="primary" />
                  ) : (
                    <Typography component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {l.short}
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Box>
          </Menu>
        </Container>
      </Box>

      {/* Main content */}
      <Box component="main" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {children}
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ backgroundColor: headerFooterBg, flexShrink: 0, py: 1.5 }}>
        <Container maxWidth="sm" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2, sm: 3 } }}>
          <Typography variant="caption" sx={{ color: alpha(headerFooterText, 0.7) }}>
            v{GlobalConfig.val().Version}
          </Typography>
          <img src="/images/logo-banqsoft-gray.png" alt="Banqsoft" style={{ height: 24, width: 'auto', opacity: 0.7 }} />
        </Container>
      </Box>

    </Box>
  );
};

export default CaseStatusLayout;
